import { inject, Injectable } from '@angular/core';
import { type CombinedGraphQLErrors } from '@apollo/client';
import { TranslateService } from '@ngx-translate/core';
import { pathIncludes } from '@reconcept-platform/shared/data-access';
import { Subject } from 'rxjs';

export enum ToastNotificationStyleEnum {
  success = 'success',
  error = 'error',
  alert = 'alert',
  info = 'info',
}

export interface ToastNotification {
  style: ToastNotificationStyleEnum;
  title: string;
  message: string;
  errorCode?: string;
  duration: number;
  closeable?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToastNotificationsService {
  private translateService = inject(TranslateService);
  notifications: ToastNotification[] = [];
  notify = new Subject<ToastNotification[]>();
  notifications$ = this.notify.asObservable();

  // currently, the generated form control generates only one error, even if multiple fields are invalid
  showFormErrors(error: { type: 'custom' | 'default'; message?: string; field: string }) {
    // show custom validator error
    if (error.type === 'custom') {
      // @ts-expect-error TS2345 argument not assignable
      this.error(error.message);
    }

    if (error.type === 'default') {
      this.errorTranslated({
        titleKey: 'qz-field-X-has-not-been-filled-in-zm',
        titleInterpolateParams: { field: error.field },
      });
    }
  }

  // currently, the generated form control generates only one error, even if multiple fields are invalid
  showCustomError(error: Error) {
    // only show error if it's a default error
    if (error.message) {
      this.error(error.message);
    }
  }

  // tries to give the right message for errors
  showParsedGraphQLError(error: CombinedGraphQLErrors, defaultErrorMessage?: string) {
    if (!error) {
      return;
    }
    const specificErrorMessage = this.getMessageFromGraphQLError(error);
    this.error(
      specificErrorMessage ?? defaultErrorMessage ?? this.translateService.instant('qz-unexpected-error-occurred-zm'),
    );
  }

  // show toast methods
  success(title: string, message?: string) {
    // @ts-expect-error TS2345 argument not assignable
    this.pushNotification(ToastNotificationStyleEnum.success, title, message, undefined, 2000);
  }

  successTranslated({
    titleKey,
    messageKey,
    titleInterpolateParams,
    messageInterpolateParams,
  }: {
    titleKey: string;
    messageKey?: string;
    titleInterpolateParams?: object;
    messageInterpolateParams?: object;
  }) {
    const title = this.translateService.instant(titleKey, titleInterpolateParams);
    const message = messageKey && this.translateService.instant(messageKey, messageInterpolateParams);
    this.success(title, message);
  }

  error(title: string, message?: string, errorCode?: string) {
    // @ts-expect-error TS2345 argument not assignable
    this.pushNotification(ToastNotificationStyleEnum.error, title, message, errorCode, 0);
  }

  errorTranslated({
    titleKey,
    messageKey,
    errorCode,
    titleInterpolateParams,
    messageInterpolateParams,
  }: {
    titleKey: string;
    messageKey?: string;
    errorCode?: string;
    titleInterpolateParams?: object;
    messageInterpolateParams?: object;
  }) {
    const title = this.translateService.instant(titleKey, titleInterpolateParams);
    const message = messageKey && this.translateService.instant(messageKey, messageInterpolateParams);
    this.error(title, message, errorCode);
  }

  alert(title: string, message?: string) {
    // @ts-expect-error TS2345 argument not assignable
    this.pushNotification(ToastNotificationStyleEnum.alert, title, message, undefined, 2000);
  }

  alertTranslated({
    titleKey,
    messageKey,
    titleInterpolateParams,
    messageInterpolateParams,
  }: {
    titleKey: string;
    messageKey?: string;
    titleInterpolateParams?: object;
    messageInterpolateParams?: object;
  }) {
    const title = this.translateService.instant(titleKey, titleInterpolateParams);
    const message = messageKey && this.translateService.instant(messageKey, messageInterpolateParams);
    this.alert(title, message);
  }

  info(title: string, message?: string) {
    // @ts-expect-error TS2345 argument not assignable
    this.pushNotification(ToastNotificationStyleEnum.info, title, message, undefined, 2000);
  }

  infoTranslated({
    titleKey,
    messageKey,
    titleInterpolateParams,
    messageInterpolateParams,
  }: {
    titleKey: string;
    messageKey?: string;
    titleInterpolateParams?: object;
    messageInterpolateParams?: object;
  }) {
    const title = this.translateService.instant(titleKey, titleInterpolateParams);
    const message = messageKey && this.translateService.instant(messageKey, messageInterpolateParams);
    this.info(title, message);
  }

  close(notification: ToastNotification) {
    // the timeout also closes a toast the user closed already
    const index = this.notifications.indexOf(notification);
    if (index === -1) {
      return;
    }

    this.notifications.splice(index, 1);
    this.notifications = [...this.notifications];
    this.notify.next(this.notifications);
  }

  globalFormError() {
    this.error(this.translateService.instant('qz-something-went-wrong-please-check-form-zm'));
  }

  // produce one human-readable error from the errors object
  private getMessageFromGraphQLError(error: CombinedGraphQLErrors): string {
    if (!error) {
      // @ts-expect-error TS2322 type not assignable
      return null;
    }

    if (pathIncludes(error, 'ownerPin')) {
      return this.translateService.instant('qz-pin-owner-incorrect-zm');
    }

    if (pathIncludes(error, 'supervisorPin')) {
      return this.translateService.instant('qz-pin-supervisor-incorrect-zm');
    }

    if (pathIncludes(error, 'email')) {
      return this.translateService.instant('qz-email-address-invalid-or-already-used-zm');
    }

    if (error.message) {
      return error.message;
    }

    // @ts-expect-error TS2322 type not assignable
    return null;
  }

  private pushNotification(
    style: ToastNotificationStyleEnum,
    title: string,
    message: string,
    errorCode: string,
    duration: number,
  ) {
    const newNotification: ToastNotification = {
      style,
      title,
      message,
      errorCode,
      duration,
      closeable: duration > 0,
    };

    // only push if notification title not yet in notifications
    const notificationShown = this.notifications.findIndex(
      (notification) => notification.title === newNotification.title,
    );

    if (notificationShown === -1) {
      this.notifications.push(newNotification);
      this.notify.next(this.notifications);

      if (newNotification.duration) {
        // setTimeout to remove
        setTimeout(() => {
          this.close(newNotification);
        }, newNotification.duration + 300); //extra 300ms for animation}
      }
    }
  }
}
